import { Permission } from 'api/PermissionsAPI';
import { CAMPAIGN_MEDIUMS } from 'modules/roles/constants';
import { MergedPermission } from 'modules/roles/types';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';
import { Table } from 'ui/table';
import { TableCell } from 'ui/tableCell';
import { TableRows } from 'ui/tableRows';
import { keyBy } from 'utils/arrayHelpers';

const PermissionsCell = styled.div`
  justify-self: flex-end;
`;

const PermissionCount = styled.div`
  text-decoration: underline dotted;
`;

const StyledTableWrapper = styled.div`
  max-height: 100%;
  overflow: auto;
`;

const StyledList = styled.ul`
  padding: 0;
  margin: 0;
`;

type ListItem = {
  id: string;
  title: ReactNode;
  children?: ListItem[];
};

type RowContent = {
  row: { id: string; category: string; list: ListItem[]; values: string[] };
};

export const cols = [
  {
    name: 'Category',
    value: 'category',
    RowCell: ({ row }: RowContent) => (
      <TableCell>
        <strong>{row.category}</strong>
      </TableCell>
    ),
  },
  {
    name: 'Values',
    value: 'values',
    RowCell: ({ row }: RowContent) => (
      <TableCell>
        <StyledList>
          {row.values.map((val) => (
            <li key={val}>{val}</li>
          ))}
        </StyledList>
      </TableCell>
    ),
  },
];

type Props = {
  getPermissionById: (id: number) => Permission | undefined;
  permissionsList: number[];
};

const isViewPermission = (permission: MergedPermission) => {
  if (!permission) return false;
  return permission.requires.length === 0 && permission.requiredBy.length > 0;
};

const isDraftPermission = (permission: MergedPermission) => {
  if (!permission) return false;
  return permission.requires.length > 0 && permission.requiredBy.length > 0;
};

const createRows = ({ getPermissionById, permissionsList }: Props) => {
  const permissionDetails = permissionsList.map((id) => getPermissionById(id));
  const currentPermissionsByCategory = keyBy(
    permissionDetails,
    (permission) => permission?.category,
  );

  /** In some cases getPermissionById returns only undefined, which causes a page crash.
   * Longer term we could consider refactoring queries in PermissionsContextProvider, but
   * this resolves the pressing crash.
   */
  if (!permissionDetails.filter(Boolean).length) return [];

  const rows = Object.keys(currentPermissionsByCategory).map((category) => {
    const categoryPermissions = currentPermissionsByCategory[category] as MergedPermission[];
    const initialRows: { [key: string]: MergedPermission[] } = {
      view: [],
      draft: [],
      permissions: [],
      childPermissions: [],
    };

    const reduced = categoryPermissions.reduce((acc, curr) => {
      const isView = isViewPermission(curr);
      const isDraft = isDraftPermission(curr);
      const isChild = CAMPAIGN_MEDIUMS.includes(curr.id);

      if (isView) {
        acc.view.push(curr);
        return acc;
      }
      if (isDraft) {
        acc.draft.push(curr);
        return acc;
      }
      if (isChild) {
        acc.childPermissions.push(curr);
        return acc;
      }

      acc.permissions.push(curr);
      return acc;
    }, initialRows);

    const { draft, childPermissions, permissions, view } = reduced;
    const childNames =
      childPermissions?.length > 0
        ? `Manage and launch campaigns (${childPermissions.map((p) => p.name).join(', ')})`
        : '';

    const values = [
      ...view?.map((p) => p.name),
      ...draft?.map((p) => p.name),
      ...permissions?.map((p) => p.name),
      childNames,
    ].filter((val) => !!val);

    return {
      id: category,
      category,
      values,
    };
  });

  return rows;
};

export const PermissionsTableCell = ({ getPermissionById, permissionsList }: Props) => {
  const rows = createRows({ getPermissionById, permissionsList });
  return (
    <PermissionsCell>
      {permissionsList.length > 0 ? (
        <Popover>
          <PopoverContent side="left">
            <StyledTableWrapper>
              <Table>
                <TableRows rows={rows} columns={cols} />
              </Table>
            </StyledTableWrapper>
          </PopoverContent>
          <PopoverTrigger>
            <PermissionCount>{permissionsList.length}</PermissionCount>
          </PopoverTrigger>
        </Popover>
      ) : (
        <span>{permissionsList.length}</span>
      )}
    </PermissionsCell>
  );
};
